import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';

const EMPTY = {
  id: null,
  power_plant_id: '',
  customer_id: '',
  apelido: '',
  numero_instalacao_enel: '',
  endereco: '',
  ativa: true,
};

const SolarConsumerUnitsPage = () => {
  const [items, setItems] = useState([]);
  const [plants, setPlants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({
    search: '',
    status: 'TODAS',
    power_plant_id: '',
  });

  const loadLookups = async () => {
    try {
      const [plantsRs, customersRs] = await Promise.all([
        axios.get('/api/solar/power-plants'),
        axios.get('/api/customers', { params: { pageSize: 200, ativo: true } }),
      ]);
      setPlants(Array.isArray(plantsRs.data?.items) ? plantsRs.data.items : []);
      setCustomers(Array.isArray(customersRs.data?.items) ? customersRs.data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar cadastros base');
    }
  };

  const loadUnits = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/solar/consumer-units');
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar unidades consumidoras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups();
    loadUnits();
  }, []);

  const filteredItems = useMemo(() => {
    const search = String(filters.search || '').trim().toLowerCase();
    return items.filter((item) => {
      if (filters.status === 'ATIVAS' && item.ativa !== true) return false;
      if (filters.status === 'INATIVAS' && item.ativa !== false) return false;
      if (filters.power_plant_id && item.power_plant_id !== filters.power_plant_id) return false;
      if (!search) return true;

      const haystack = [item.apelido, item.numero_instalacao_enel, item.customer_nome, item.power_plant_nome]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(search);
    });
  }, [filters.power_plant_id, filters.search, filters.status, items]);

  const openNew = () => {
    setForm(EMPTY);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      power_plant_id: item.power_plant_id,
      customer_id: item.customer_id,
      apelido: item.apelido || '',
      numero_instalacao_enel: item.numero_instalacao_enel || '',
      endereco: item.endereco || '',
      ativa: item.ativa !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      const payload = {
        power_plant_id: form.power_plant_id,
        customer_id: form.customer_id,
        apelido: form.apelido || null,
        numero_instalacao_enel: form.numero_instalacao_enel,
        endereco: form.endereco || null,
        ativa: form.ativa,
      };

      if (!payload.power_plant_id || !payload.customer_id || !payload.numero_instalacao_enel) {
        toast.error('Usina, cliente e instalacao ENEL sao obrigatorios');
        return;
      }

      if (form.id) {
        await axios.put(`/api/solar/consumer-units/${form.id}`, payload);
        toast.success('Unidade consumidora atualizada');
      } else {
        await axios.post('/api/solar/consumer-units', payload);
        toast.success('Unidade consumidora criada');
      }

      setShowModal(false);
      setForm(EMPTY);
      await loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar unidade consumidora');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar · Unidades</h1>
          <p className="text-gray-600">Vinculo entre usinas, UCs e clientes</p>
        </div>
        <div className="flex gap-3 items-center">
          {loading && <div className="loader h-6 w-6" />}
          <button className="btn-primary" onClick={openNew}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova Unidade
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-5 gap-3 border-b">
          <div className="md:col-span-2">
            <label className="form-label">Buscar</label>
            <input
              className="form-input"
              placeholder="Apelido, instalacao, cliente ou usina"
              value={filters.search}
              onChange={(e) => setFilters((old) => ({ ...old, search: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Usina</label>
            <select
              className="form-select"
              value={filters.power_plant_id}
              onChange={(e) => setFilters((old) => ({ ...old, power_plant_id: e.target.value }))}
            >
              <option value="">Todas</option>
              {plants.map((plant) => (
                <option key={plant.id} value={plant.id}>
                  {plant.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={filters.status} onChange={(e) => setFilters((old) => ({ ...old, status: e.target.value }))}>
              <option value="TODAS">Todas</option>
              <option value="ATIVAS">Ativas</option>
              <option value="INATIVAS">Inativas</option>
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn-secondary w-full" onClick={() => setFilters({ search: '', status: 'TODAS', power_plant_id: '' })}>
              Limpar
            </button>
          </div>
        </div>
        <div className="card-header">Unidades Consumidoras ({filteredItems.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Apelido</th>
                  <th>Instalacao ENEL</th>
                  <th>Cliente</th>
                  <th>Usina</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.apelido || '-'}</td>
                    <td>{item.numero_instalacao_enel}</td>
                    <td>{item.customer_nome || '-'}</td>
                    <td>{item.power_plant_nome || '-'}</td>
                    <td>
                      <span className={item.ativa ? 'badge-success' : 'badge-danger'}>{item.ativa ? 'Ativa' : 'Inativa'}</span>
                    </td>
                    <td>
                      <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => openEdit(item)}>
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-8">
                      Nenhuma unidade cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar Unidade Consumidora' : 'Nova Unidade Consumidora'}</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Usina</label>
                <select className="form-select" value={form.power_plant_id} onChange={(e) => setForm({ ...form, power_plant_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {plants.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Cliente</label>
                <select className="form-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Apelido</label>
                <input className="form-input" value={form.apelido} onChange={(e) => setForm({ ...form, apelido: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Numero Instalacao ENEL</label>
                <input
                  className="form-input"
                  value={form.numero_instalacao_enel}
                  onChange={(e) => setForm({ ...form, numero_instalacao_enel: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Endereco</label>
                <input className="form-input" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.ativa} onChange={(e) => setForm({ ...form, ativa: e.target.checked })} />
                  Unidade ativa
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={save}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarConsumerUnitsPage;

